"use client";
import { useRef, useEffect, useState, useCallback } from "react";
import clsx from "clsx";
import { cva } from "class-variance-authority";
import { useIsWidthMin } from "@ui/hooks/useBreakpoint";

interface LegendScrollerProps {
  titles: string[];
  leftmostIndex: number;
  onItemClick: (index: number) => void;
  /** Custom class name for legend item text styling (overrides default f-paragraph-03) */
  textClassName?: string;
  columns: 3 | 4 | 5;
}

const legendItemVariants = cva(
  [
    "tw-flex-none",
    "tw-w-[calc((100%-1*0.125rem)/2)] tw-max-w-50 tw-items-center",
    "sm:tw-w-[calc((100%-2*0.25rem)/3)]",
    "md:tw-w-[calc((100%-4*0.25rem)/5)]",
    "tw-px-3 tw-py-1.5 tw-rounded-full tw-truncate",
    "tw-transition-all tw-duration-300",
    "tw-opacity-40 hover:tw-opacity-70",
  ],
  {
    variants: {
      columns: {
        3: null,
        4: null,
        5: "min-[32rem]:tw-w-[calc((100%-2*0.25rem)/3)]",
      },
      showOnDesktop: {
        three: "xl:tw-w-[calc((100%-2*0.5rem)/3)]",
        four: "xl:tw-w-[calc((100%-3*0.5rem)/4)]",
        five: "xl:tw-w-[calc((100%-4*0.5rem)/5)]",
        seven: "xl:tw-w-[calc((100%-6*0.5rem)/7)]",
        nine: "xl:tw-w-[calc((100%-8*0.5rem)/9)]",
      },
      isMobileActive: {
        true: "max-sm:tw-opacity-100",
      },
      isXsActive: {
        true: "min-[32rem]:tw-opacity-100",
      },
      isSmActive: {
        true: "sm:tw-opacity-100",
      },
      isMdActive: {
        true: "md:tw-opacity-100",
      },
      isLgActive: {
        true: "lg:tw-opacity-100",
      },
      isXlActive: {
        true: "xl:tw-opacity-100",
      },
    },
    defaultVariants: {
      showOnDesktop: "five",
    },
  },
);

const LEGEND_SHOW_ON_DESKTOP_CONFIG = {
  3: {
    showOnDesktop: "five",
  },
  4: {
    showOnDesktop: "seven",
  },
  5: {
    showOnDesktop: "nine",
  },
} as const;

export function LegendScroller({ titles, leftmostIndex, onItemClick, textClassName, columns }: LegendScrollerProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  // Use matchMedia hooks for responsive breakpoint detection (more efficient than tracking window width)
  const isMobile = useIsWidthMin("md");

  // Check if we can scroll in either direction
  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const currentScroll = scroller.scrollLeft;

    setCanScrollPrev(currentScroll > 10);
    setCanScrollNext(currentScroll < maxScroll - 10);
  }, []);

  // Auto-scroll to keep active items centered
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const itemToCenter = isMobile ? leftmostIndex : leftmostIndex + 1;
    const itemWidth = scroller.scrollWidth / titles.length;
    const targetScroll = itemToCenter * itemWidth - scroller.clientWidth / 2 + itemWidth / 2;

    scroller.scrollTo({ left: targetScroll, behavior: "smooth" });
  }, [leftmostIndex, titles.length, isMobile]);

  // Track scroll position for fade overlays
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    updateScrollState();
    scroller.addEventListener("scroll", updateScrollState);
    return () => scroller.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  const { showOnDesktop } = LEGEND_SHOW_ON_DESKTOP_CONFIG[columns];

  return (
    <div className='tw-relative tw-flow-root'>
      {/* Left fade overlay - shown when scrolled away from start */}
      <div
        className={clsx(
          "tw-absolute tw-top-0 tw-bottom-0 tw-w-16 tw-z-10 tw-pointer-events-none -tw-left-full-bleed tw-transition-opacity tw-duration-300",
          !canScrollPrev && "tw-opacity-0",
        )}
        style={{
          background: "linear-gradient(to right, var(--marker-background-color, white), transparent)",
        }}
      />

      {/* Right fade overlay - shown when can scroll further */}
      <div
        className={clsx(
          "tw-absolute tw-top-0 tw-bottom-0 tw-w-16 tw-z-10 tw-pointer-events-none -tw-right-full-bleed tw-transition-opacity tw-duration-300",
          !canScrollNext && "tw-opacity-0",
        )}
        style={{
          background: "linear-gradient(to left, var(--marker-background-color, white), transparent)",
        }}
      />

      <div
        ref={scrollerRef}
        className='tw-flex tw-gap-0.5 md:tw-gap-1 xl:tw-gap-2 tw-overflow-x-auto tw-scrollbar-none -tw-mx-full-bleed tw-px-full-bleed'
      >
        {titles.map((title, index) => {
          const isMobileActive = index === leftmostIndex;
          const isXsActive = index >= leftmostIndex && index <= leftmostIndex + { 3: 0, 4: 0, 5: 1 }[columns];
          const isSmActive = index >= leftmostIndex && index <= leftmostIndex + { 3: 1, 4: 1, 5: 1 }[columns];
          const isMdActive = index >= leftmostIndex && index <= leftmostIndex + { 3: 1, 4: 1, 5: 2 }[columns];
          const isLgActive = index >= leftmostIndex && index <= leftmostIndex + { 3: 2, 4: 2, 5: 3 }[columns];
          const isXlActive = index >= leftmostIndex && index <= leftmostIndex + { 3: 2, 4: 3, 5: 4 }[columns];

          return (
            <button
              key={index}
              onClick={() => onItemClick(index)}
              className={clsx(
                legendItemVariants({
                  columns,
                  showOnDesktop,
                  isMobileActive,
                  isXsActive,
                  isSmActive,
                  isMdActive,
                  isLgActive,
                  isXlActive,
                }),
                textClassName ?? "tw-type-sm",
              )}
            >
              {title}
            </button>
          );
        })}
      </div>
    </div>
  );
}
